/**
 * Style-based recommendations for shapes, colors, and materials
 * These are shown as suggestions when a user selects a specific style
 */

import type { StylePreset } from './stylePresets';

export type StyleRecommendation = {
	styleKey: StylePreset['key'];
	recommendations: {
		shapes?: string[];
		colors?: string[];
		materials?: string[];
		poolTileColors?: string[];
		fenceColors?: string[];
		deckColors?: string[];
		generalNotes?: string;
	};
};

/**
 * Style-based recommendations
 * These help guide users toward choices that match their selected style
 */
export const styleRecommendations: StyleRecommendation[] = [
	{
		styleKey: 'modern',
		recommendations: {
			shapes: ['Rectangular', 'Square', 'Geometric'],
			colors: ['Neutral tones', 'Gray', 'Black', 'White', 'Minimal color palette'],
			materials: ['Concrete', 'Steel', 'Glass', 'Sleek wood'],
			poolTileColors: ['Blue', 'Light Blue', 'White'],
			fenceColors: ['Black', 'Gray', 'Natural Wood (stained dark)'],
			deckColors: ['Gray', 'Natural Wood (stained)', 'Dark Brown'],
			generalNotes: 'Modern style favors clean lines, geometric shapes, and neutral color palettes. Think minimalist and contemporary.'
		}
	},
	{
		styleKey: 'mediterranean',
		recommendations: {
			shapes: ['Freeform', 'Oval', 'Curved'],
			colors: ['Warm tones', 'Terracotta', 'Cream', 'Blue', 'White'],
			materials: ['Natural Stone', 'Terracotta', 'Ceramic tiles', 'Wrought iron'],
			poolTileColors: ['Blue', 'Turquoise', 'Light Blue', 'White'],
			fenceColors: ['Natural Wood', 'White', 'Terracotta'],
			deckColors: ['Natural Wood', 'Terracotta', 'Stone'],
			generalNotes: 'Mediterranean style is warm, dry-climate inspired: terracotta, olive tones, ceramic tiles, wrought iron. Think Italy, Greece, Spain—sun-baked, not beachy.'
		}
	},
	{
		styleKey: 'minimalist',
		recommendations: {
			shapes: ['Rectangular', 'Square', 'Simple geometric'],
			colors: ['Monochrome', 'Neutral', 'White', 'Gray', 'Black'],
			materials: ['Concrete', 'Simple wood', 'Metal', 'Stone'],
			poolTileColors: ['White', 'Light Blue', 'Blue'],
			fenceColors: ['White', 'Gray', 'Natural Wood (light)'],
			deckColors: ['Natural Wood (light)', 'Gray', 'White'],
			generalNotes: 'Minimalist style emphasizes simplicity, clean lines, and a limited color palette. Less is more.'
		}
	},
	{
		styleKey: 'cottage',
		recommendations: {
			shapes: ['Freeform', 'Irregular', 'Organic'],
			colors: ['Pastels', 'Soft colors', 'Natural tones', 'Mixed colors'],
			materials: ['Wood', 'Natural stone', 'Wrought iron', 'Traditional materials'],
			poolTileColors: ['Light Blue', 'Blue', 'Natural Stone'],
			fenceColors: ['Natural Wood', 'White', 'Painted (pastel colors)'],
			deckColors: ['Natural Wood', 'Stained Wood', 'Mixed materials'],
			generalNotes: 'Cottage style is informal, colorful, and features traditional materials. Think charming and relaxed.'
		}
	},
	{
		styleKey: 'japanese',
		recommendations: {
			shapes: ['Rectangular', 'Oval', 'Natural/Organic'],
			colors: ['Natural tones', 'Earth tones', 'Neutral', 'Green'],
			materials: ['Natural Stone', 'Wood', 'Bamboo', 'Gravel'],
			poolTileColors: ['Natural Stone', 'Dark Blue', 'Neutral tones'],
			fenceColors: ['Natural Wood', 'Bamboo', 'Natural materials'],
			deckColors: ['Natural Wood', 'Stone', 'Gravel'],
			generalNotes: 'Japanese Zen style is contemplative and minimal: raked gravel, stones, bamboo, still water, moss. Think calm, ordered, and in harmony with nature.'
		}
	},
	{
		styleKey: 'tropical',
		recommendations: {
			shapes: ['Freeform', 'Organic', 'Curved'],
			colors: ['Greens', 'Vibrant accents', 'Natural tones', 'Tropical flowers'],
			materials: ['Natural Stone', 'Wood', 'Bamboo', 'Rattan'],
			poolTileColors: ['Blue', 'Turquoise', 'Natural Stone'],
			fenceColors: ['Natural Wood', 'Bamboo', 'Dark stained wood'],
			deckColors: ['Natural Wood', 'Teak', 'Bamboo'],
			generalNotes: 'Tropical style features lush foliage, bold leaves, palms, and vibrant accents. Think exotic and relaxing.'
		}
	},
	{
		styleKey: 'rustic',
		recommendations: {
			shapes: ['Irregular', 'Organic', 'Freeform'],
			colors: ['Earth tones', 'Browns', 'Greens', 'Natural wood tones'],
			materials: ['Weathered Wood', 'Stone', 'Metal', 'Reclaimed materials'],
			poolTileColors: ['Natural Stone', 'Earth tones', 'Dark Blue'],
			fenceColors: ['Weathered Wood', 'Natural Wood', 'Metal (black)'],
			deckColors: ['Weathered Wood', 'Natural Wood', 'Stone'],
			generalNotes: 'Rustic style uses weathered wood, natural stone, and country charm. Think farmhouse and outdoorsy.'
		}
	},
	{
		styleKey: 'coastal',
		recommendations: {
			shapes: ['Freeform', 'Curved', 'Organic'],
			colors: ['Blues', 'Whites', 'Sand', 'Soft neutrals'],
			materials: ['Driftwood', 'Weathered wood', 'Stone', 'Rope', 'Rattan'],
			poolTileColors: ['Light Blue', 'White', 'Turquoise'],
			fenceColors: ['White', 'Weathered Wood', 'Natural Wood'],
			deckColors: ['Weathered Wood', 'Gray', 'White'],
			generalNotes: 'Coastal style is beach/ocean inspired: driftwood, dune grasses, soft blues and whites, nautical touches. Think seaside, breezy—different from Mediterranean’s warm dry-climate look.'
		}
	},
	{
		styleKey: 'scandinavian',
		recommendations: {
			shapes: ['Rectangular', 'Simple geometric', 'Clean lines'],
			colors: ['White', 'Light wood', 'Gray', 'Soft greens', 'Neutral'],
			materials: ['Light Wood', 'Concrete', 'Stone', 'Metal'],
			poolTileColors: ['White', 'Light Blue', 'Gray'],
			fenceColors: ['White', 'Light Wood', 'Gray'],
			deckColors: ['Light Wood', 'Gray', 'White'],
			generalNotes: 'Scandinavian style is light, simple, and hygge-inspired. Think clean lines, natural light, and understated elegance.'
		}
	},
	{
		styleKey: 'traditional',
		recommendations: {
			shapes: ['Rectangular', 'Symmetrical', 'Formal borders'],
			colors: ['Greens', 'Roses', 'Classic pastels', 'Natural tones'],
			materials: ['Brick', 'Stone', 'Wood', 'Wrought iron'],
			poolTileColors: ['Blue', 'Light Blue', 'Natural Stone'],
			fenceColors: ['Natural Wood', 'White', 'Black (wrought iron)'],
			deckColors: ['Natural Wood', 'Stone', 'Brick'],
			generalNotes: 'Traditional style features classic borders, hedges, roses, and formal touches. Think English garden and timeless.'
		}
	}
];

/**
 * Get recommendations for a specific style
 */
export function getStyleRecommendations(styleKey: StylePreset['key']): StyleRecommendation['recommendations'] | null {
	const recommendation = styleRecommendations.find(sr => sr.styleKey === styleKey);
	return recommendation?.recommendations || null;
}

/**
 * Format recommendations as a readable string for display
 */
export function formatStyleRecommendations(styleKey: StylePreset['key']): string {
	const recs = getStyleRecommendations(styleKey);
	if (!recs) return '';
	
	const parts: string[] = [];
	if (recs.shapes && recs.shapes.length > 0) {
		parts.push(`Recommended shapes: ${recs.shapes.join(', ')}`);
	}
	if (recs.colors && recs.colors.length > 0) {
		parts.push(`Recommended colors: ${recs.colors.join(', ')}`);
	}
	if (recs.materials && recs.materials.length > 0) {
		parts.push(`Recommended materials: ${recs.materials.join(', ')}`);
	}
	if (recs.generalNotes) {
		parts.push(recs.generalNotes);
	}
	
	return parts.join('. ') + '.';
}
